import React, { useEffect, useContext, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import ScreenLoading from 'components/ScreenLoading'
import FileUpload from 'components/FileUpload/FileUpload'
import Modal from 'components/Modal'

import useGetAuthUrl from 'containers/services/useGetAuthUrl'
import useGetServices from 'containers/services/useGetServices'
import OrganizationsContext from 'containers/organizations/organizationsContext'

import './AddSource.scss'

import * as rudderanalytics from 'rudder-sdk-js'
import Feature from 'components/Feature'
import TagManager from 'react-gtm-module'
import {
  shopifyTileDestination,
  SHOPIFY_PUBLIC_SERVICE_ID,
  SHOPIFY_PRIVATE_APP_SERVICE_ID,
} from 'containers/shopify/shopifyTileDestination'

const AddServiceButton = ({ service, handleConnectService, loadingAuthUrlServiceId, connectDisabled, disabled = false}) => {
  const history = useHistory();
  const [showPopup, setShowPopup] = useState(false)
  const [manualUploadCategory, setManualUploadCategory] = useState(null)
  // Confirmation before we hand the merchant off to Shopify (see the 'shopify' case below).
  const [showShopifyRedirect, setShowShopifyRedirect] = useState(false)
  const {currentOrganization} = useContext(OrganizationsContext)

  const handleClick = () => {
    if (disabled) {
      history.push(`/organization/${currentOrganization.slug}/billing/details?checkout=1`)
      return
    }
    const traits = {
      service_id: service.id,
      service__is_listed: service.is_listed,
      service_name: service.name,
      service_category: service.category,
    }
    TagManager.dataLayer({ dataLayer: { 
      event: 'new_source_button_clicked',
      is_custom_track: true,
      event_params: {
        serviceId: service.id,
        serviceIsListed: service.is_listed,
        serviceMame: service.name,
        serviceCategory: service.category,
      }
    }})
    // rudderanalytics.track('new_source_button_clicked', traits)

    localStorage.removeItem('serviceAccounts')

    switch (service.id) {
      case 'google_play':
        history.push(`setup/google_play/${service.uuid}`)
        break
      case 'apple_ads':
        history.push(`setup/apple_ads/${service.uuid}`)
        break
      case 'apple_app_store':
        history.push(`setup/apple_app_store/${service.uuid}`)
        break
      case 'google_analytics_4_events':
        history.push(`setup/google_analytics_4_events/${service.uuid}`) 
        break
      case 'moosend':
        history.push(`setup/moosend/${service.uuid}`)
        break
      case 'klaviyo':
        history.push(`setup/klaviyo/${service.uuid}`)
        break
      // Both Shopify tiles: the destination itself lives in shopifyTileDestination (pure and
      // unit-tested) — the public app installs from the App Store because that install is
      // what hands us the shop domain and OAuth grant Shopify billing requires, while the
      // private app is a credentials form. Only the navigation is decided here.
      //
      // The public tile confirms before leaving. Leaving the app deserves a heads-up, and the
      // pause also protects analytics: new_source_button_clicked went onto the GTM dataLayer a
      // few lines up, and while dataLayer.push is synchronous, GTM evaluates and sends the tag
      // asynchronously — unloading the document in the same tick can cancel the request before
      // it leaves. The merchant reading the modal is far more time than GTM needs.
      case SHOPIFY_PUBLIC_SERVICE_ID:
        setShowShopifyRedirect(true)
        break
      case SHOPIFY_PRIVATE_APP_SERVICE_ID: {
        const destination = shopifyTileDestination(service)
        history.push(destination.path)
        break
      }
      case 'manual_orders':
      case 'manual_users':
        history.push(`setup/custom-table/${service.id}`)
        break
      case 'manual_expenses':
        // Expenses are managed on the Manual Expenses page, not as a custom table
        history.push(`/organization/${currentOrganization.slug}/expenses`)
        break
      case 'manual_search_term':
        // Manual queries are managed on their own page (documents list + upload)
        history.push(`/organization/${currentOrganization.slug}/manual-queries`)
        break
      case 'manual_ads_performance':
        if (showPopup === false) {
          setShowPopup(true)
          setManualUploadCategory('manual_ads_performance')
        }
        break
      case 'manual_ads_conversions':
        if (showPopup === false) {
          setShowPopup(true)
          setManualUploadCategory('manual_ads_conversions')
        }
        break
      default:
        handleConnectService(service.uuid)
    }
  }

  return (
    <>
      <div className={`source-button ${connectDisabled || disabled ? 'disabled' : ''}`} onClick={handleClick}>
        <div className='info'>
          {loadingAuthUrlServiceId === service.id ?
            <ScreenLoading /> : (
            <>
              {MANUAL_TILES[service.id] ? (
                <div className={`manual-tile ${MANUAL_TILES[service.id].tone}`}>
                  <span className="material-icons-outlined">
                    {MANUAL_TILES[service.id].icon}
                  </span>
                </div>
              ) : (
                <img alt={service.id} width="90" src={service.logo}></img>
              )}
              <span className="name">{service.name}</span>
            </>
          )}
        </div>
        <button className={`link ${disabled ? "upgrade-btn" : ""}`}>{disabled ? <span>Upgrade plan</span> : <><span className='enlarge'>+</span>Connect</>}</button>
        {manualUploadCategory !== null && <FileUpload show={showPopup} onHide={() => setShowPopup(false)} category={manualUploadCategory}/>}
      </div>

    {/* Sibling of the tile, never a child: Modal portals out of the DOM, but React still
        bubbles its clicks up the COMPONENT tree — nested, a "Continue" click would also
        re-fire the tile's onClick and push a second GTM event. Mounted only for the one
        Shopify tile (an .overlay without .show is display:none, so it is inert until used). */}
    {service.id === SHOPIFY_PUBLIC_SERVICE_ID && (
      <Modal
        className="shopify-redirect-modal"
        show={showShopifyRedirect}
        onHide={() => setShowShopifyRedirect(false)}
        styles={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxWidth: '480px',
          padding: '32px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: 0 }}>Continue to the Shopify App Store</h2>
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          Maya Insights is installed from Shopify’s App Store — the install is what tells us
          which store to sync and grants permission to read its data. Choose your store there
          and you’ll come straight back here to pick a plan.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            className="submit-button"
            style={{ width: 'auto', padding: '0 24px', marginTop: 0 }}
            onClick={() => window.location.assign(shopifyTileDestination(service).url)}
          >
            Continue to Shopify
          </button>
          <button
            className="submit-button inverted"
            style={{ width: 'auto', padding: '0 24px', marginTop: 0 }}
            onClick={() => setShowShopifyRedirect(false)}
          >
            Cancel
          </button>
        </div>
      </Modal>
    )}
    </>
  )
}

// Manual services show a code-drawn icon tile instead of the hosted logo image:
// the dashed border reads as "a slot you fill in yourself" (it solidifies on
// hover), separating hand-entered data from the logo'd third-party integrations.
// Icon names must exist in material-icons@0.5.4 — prefer long-established glyphs.
const MANUAL_TILES = {
  manual_expenses: { icon: 'payments', tone: 'green' },
  manual_orders: { icon: 'shopping_cart', tone: 'amber' },
  manual_users: { icon: 'group', tone: 'blue' },
  manual_search_term: { icon: 'search', tone: 'purple' },
  manual_ads_performance: { icon: 'campaign', tone: 'rose' },
  manual_ads_conversions: { icon: 'touch_app', tone: 'teal' },
}

// Manual Expenses is not backed by a Service row in every environment (and the
// 'Manual' category is hidden below), so its Add Source card is hardcoded. The
// AddServiceButton switch routes it to the /expenses pages instead of OAuth.
const MANUAL_EXPENSES_SERVICE = {
  id: 'manual_expenses',
  uuid: 'manual_expenses',
  name: 'Manual Expenses',
  category: 'Manual Uploads', // existing prod category; merges with its DB services
  is_listed: true,
  // no logo: MANUAL_TILES always provides this card's icon
}

const ServiceCategory = ({
  categoryName,
  category,
  handleConnectService,
  loadingAuthUrlServiceId,
  connectDisabled,
}) => {
  return (
    <div className='integrations-category'>
      <h3 className='title'>{categoryName}</h3>
      <div className='services'>
        {category.map(service =>
            // Manual Expenses is a built-in feature (no account, no plan gate) —
            // routing it through the integrations gate would show "Upgrade plan".
            service.id === MANUAL_EXPENSES_SERVICE.id ? (
              <AddServiceButton key={service.id} service={service}
                                handleConnectService={handleConnectService}
                                loadingAuthUrlServiceId={loadingAuthUrlServiceId}
                                connectDisabled={connectDisabled} />
            ) : (
            <Feature feature={(features) => features.is_trial || features.integrations !== undefined && features.integrations.find(integration => integration === service.id)} switchComponent={<AddServiceButton key={service.id} service={service} handleConnectService={handleConnectService} loadingAuthUrlServiceId={loadingAuthUrlServiceId} connectDisabled={connectDisabled} disabled/>}>
              <AddServiceButton key={service.id} service={service}
                                handleConnectService={handleConnectService}
                                loadingAuthUrlServiceId={loadingAuthUrlServiceId}
                                connectDisabled={connectDisabled} />
            </Feature>
            )
          )
        }
      </div>
    </div>
  )
}

const AddSource = () => {
  let history = useHistory()
  let location = useLocation()

  const { services, servicesError, getServices, loadingServices } =
    useGetServices()

  const { loadingAuthUrlServiceId, authUrlError, getAuthUrl } = useGetAuthUrl()

  const { currentOrganization, isLoadingOrganization } =
    useContext(OrganizationsContext)

  const getData = async () => {
    getServices()
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line
  }, [currentOrganization])

  if (servicesError) {
    return <>{servicesError}</>
  }

  if (loadingServices) {
    return <ScreenLoading />
  }

  // Define category order with E-commerce first
  const CATEGORY_ORDER = [
    'E-commerce',
    'Advertising',
    'Analytics',
    'CRM',
    'Accounting',
    'Miscellaneous',
    'Manual Uploads',
  ]

  let organizedServices = {}

  services
    .filter((s) => s.is_listed)
    .filter((s) => s.category !== 'Manual') // Hide Manual category
    .forEach((service) => {
      if (!organizedServices[service.category]) {
        organizedServices[service.category] = []
      }

      organizedServices[service.category].push(service)
    })

  // Always list Manual Expenses, unless a visible Service row already provides it
  // (avoids a duplicate card in environments where the DB has one).
  const hasDbManualExpenses = Object.values(organizedServices).some((list) =>
    list.some((s) => s.id === MANUAL_EXPENSES_SERVICE.id)
  )
  if (!hasDbManualExpenses) {
    if (!organizedServices[MANUAL_EXPENSES_SERVICE.category]) {
      organizedServices[MANUAL_EXPENSES_SERVICE.category] = []
    }
    organizedServices[MANUAL_EXPENSES_SERVICE.category].push(MANUAL_EXPENSES_SERVICE)
  }

  // Sort categories by CATEGORY_ORDER
  const sortedCategoryNames = CATEGORY_ORDER.filter(
    (cat) => organizedServices[cat]
  )
  // Add any remaining categories not in CATEGORY_ORDER
  Object.keys(organizedServices).forEach((cat) => {
    if (!sortedCategoryNames.includes(cat)) {
      sortedCategoryNames.push(cat)
    }
  })

  const handleBack = () => {
    history.push(`/organization/${currentOrganization.slug}/sources`)
  }

  const handleConnectService = async (serviceUuid) => {
    const url = await getAuthUrl(serviceUuid, currentOrganization.id)
    if (url) {
      window.location.replace(url)
    }
  }

  return (
    <div className="add-source">
      {location.state && location.state.error && (
        <div className="alert alert-danger" role="alert">
          Something went wrong, please try again later or try again with a
          different account. ({location.state.error})
        </div>
      )}
      {authUrlError && (
        <div className="alert alert-danger" role="alert">
          {authUrlError}
        </div>
      )}
      <span
        onClick={handleBack}
        className="material-icons back-arrow non-selectable"
      >
        arrow_back
      </span>
      <div className="head">
        <h2>Integrations</h2>
        <span className="subtitle-text">
          Select the 3rd party services you use at your company. Try to add as
          many as possible so we will extract and normalize your data in an
          optimal way
        </span>
      </div>
      <div className="content">
        {sortedCategoryNames.map((categoryName) => (
          <ServiceCategory
            key={categoryName}
            categoryName={categoryName}
            category={organizedServices[categoryName]}
            handleConnectService={handleConnectService}
            loadingAuthUrlServiceId={loadingAuthUrlServiceId}
            connectDisabled={isLoadingOrganization}
          />
        ))}
      </div>
    </div>
  )
}

export default React.memo(AddSource)
